import { muiGlyph } from "@/components/ui/mui-glyph";

// The glyphs the donor's rich-text editor draws.
//
// `RichTextEditor.tsx:7-15` imports them from `@mui/icons-material` by these
// names — `FormatBold`, `FormatItalic`, `FormatUnderlined`, `StrikethroughS`,
// `FormatListNumbered`, `FormatListBulleted` and `Link` for the toolbar, and
// `Check` for the link card's confirm — each a `styled(SvgIcon)` at its default
// `fontSize: medium`, a 24px box (`SvgIcon.js:75-78`). The card's `Close` is the
// same path as `Clear`, so it rides `MuiClearIcon` (`mui-toolbar-icons.tsx`).
//
// Atlas drew Lucide's outlines instead: stroked paths where MUI's are one solid
// fill each, which is a different drawing rather than a different weight.
//
// The paths are copied byte-for-byte from `@mui/icons-material` 9.2.0, each one
// the single `<path d>` of its `createSvgIcon`.

/** `@mui/icons-material/FormatBold.js`. */
const FORMAT_BOLD_PATH =
  "M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42M10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5";
/** `@mui/icons-material/FormatItalic.js`. */
const FORMAT_ITALIC_PATH = "M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z";
/** `@mui/icons-material/FormatUnderlined.js`. */
const FORMAT_UNDERLINED_PATH =
  "M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6m-7 2v2h14v-2z";
/** `@mui/icons-material/StrikethroughS.js`. */
const STRIKETHROUGH_S_PATH =
  "M6.85 7.08C6.85 4.37 9.45 3 12.24 3c1.64 0 3 .49 3.9 1.28.77.65 1.46 1.73 1.46 3.24h-3.01c0-.31-.05-.59-.15-.85-.29-.86-1.2-1.28-2.25-1.28-1.86 0-2.34 1.02-2.34 1.7 0 .48.25.88.74 1.21.38.25.77.48 1.41.7H7.39c-.21-.34-.54-.89-.54-1.92M21 12v-2H3v2h9.62c1.15.45 1.96.75 1.96 1.97 0 1-.81 1.67-2.28 1.67-1.54 0-2.93-.54-2.93-2.51H6.4c0 .55.08 1.13.24 1.58.81 2.29 3.29 3.3 5.67 3.3 2.27 0 5.3-.89 5.3-4.05 0-.3-.01-1.16-.48-1.94H21z";
/** `@mui/icons-material/FormatListNumbered.js`. */
const FORMAT_LIST_NUMBERED_PATH =
  "M2 17h2v.5H3v1h1v.5H2v1h3v-4H2zm1-9h1V4H2v1h1zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2zm5-6v2h14V5zm0 14h14v-2H7zm0-6h14v-2H7z";
/** `@mui/icons-material/FormatListBulleted.js`. */
const FORMAT_LIST_BULLETED_PATH =
  "M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5m0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5m0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5M7 19h14v-2H7zm0-6h14v-2H7zm0-8v2h14V5z";
/** `@mui/icons-material/Link.js`. */
const LINK_PATH =
  "M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1M8 13h8v-2H8zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5";
/** `@mui/icons-material/Check.js`. */
const CHECK_PATH = "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";

export const MuiFormatBoldIcon = muiGlyph("mui-format-bold", FORMAT_BOLD_PATH, "size-6");
export const MuiFormatItalicIcon = muiGlyph("mui-format-italic", FORMAT_ITALIC_PATH, "size-6");
export const MuiFormatUnderlinedIcon = muiGlyph(
  "mui-format-underlined",
  FORMAT_UNDERLINED_PATH,
  "size-6",
);
export const MuiStrikethroughSIcon = muiGlyph(
  "mui-strikethrough-s",
  STRIKETHROUGH_S_PATH,
  "size-6",
);
export const MuiFormatListNumberedIcon = muiGlyph(
  "mui-format-list-numbered",
  FORMAT_LIST_NUMBERED_PATH,
  "size-6",
);
export const MuiFormatListBulletedIcon = muiGlyph(
  "mui-format-list-bulleted",
  FORMAT_LIST_BULLETED_PATH,
  "size-6",
);
export const MuiLinkIcon = muiGlyph("mui-link", LINK_PATH, "size-6");
export const MuiCheckIcon = muiGlyph("mui-check", CHECK_PATH, "size-6");
